import { parse as parseContentDisposition } from 'content-disposition-header';

export const parseDataURL = (value: string) => {
  const regex = /^data:(?<contentType>[^;]+);base64,(?<data>.*)$/;
  const result = regex.exec(value);
  if (!result || !result.groups) {
    return null;
  }
  return {
    contentType: result.groups['contentType'],
    data: result.groups['data'],
    dataUrl: value,
  };
};

export const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    try {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        resolve(reader.result as string);
      });
      reader.addEventListener('abort', reject);
      reader.addEventListener('error', reject);
      reader.readAsDataURL(file);
    } catch (err) {
      reject(err);
    }
  });
};

export const readFileAsBase64 = async (file: File) => {
  const dataUrl = await readFileAsDataURL(file);
  if (!dataUrl) {
    return null;
  }
  return parseDataURL(dataUrl);
};

export const changeFileExtension = (fileName: string, newExtension: string) => {
  return fileName.replace(/\.\S+$/i, newExtension);
};

/**
 * Returns `fileName`, or the first `name (n).ext` variant that is not in `takenNames` (compared case-insensitively).
 */
export const getUniqueFileName = (fileName: string, takenNames: Iterable<string>) => {
  const taken = new Set(Array.from(takenNames, (name) => name.toLowerCase()));
  if (!taken.has(fileName.toLowerCase())) {
    return fileName;
  }

  const extensionIndex = fileName.lastIndexOf('.');
  const hasExtension = extensionIndex > 0;
  const baseName = hasExtension ? fileName.slice(0, extensionIndex) : fileName;
  const extension = hasExtension ? fileName.slice(extensionIndex) : '';

  for (let counter = 2; ; counter++) {
    const candidate = `${baseName} (${counter})${extension}`;
    if (!taken.has(candidate.toLowerCase())) {
      return candidate;
    }
  }
};

export const resizeImage = async (file: File, maxWidth?: number, maxHeight?: number): Promise<File> => {
  if (!file.type.startsWith('image/') || (!maxWidth && !maxHeight)) {
    return file;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    return file;
  }

  try {
    const image = await createImageBitmap(file);
    const { width, height } = image;
    const targetWidth = maxWidth && width > maxWidth ? maxWidth : width;
    const targetHeight = maxHeight && height > maxHeight ? maxHeight : width;
    const ratio = Math.min(targetWidth / width, targetHeight / height);
    const outputWidth = width * ratio;
    const outputHeight = height * ratio;
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    context?.drawImage(image, 0, 0, width, height, 0, 0, outputWidth, outputHeight);

    return new Promise((resolve, reject) => {
      try {
        // save as jpeg; webp would be better but isn't supported on safari (great browser! wow! -.-)
        canvas.toBlob((blob) => {
          if (blob) {
            resolve(new File([blob], changeFileExtension(file.name, '.jpg'), { type: blob.type }));
          } else {
            reject(new Error('canvas.toBlob failed.'));
          }
        }, 'image/jpeg');
      } catch (err) {
        reject(err);
      }
    });
  } catch (err) {
    console.error(err);
    return file;
  }
};

export const downloadFileFromUrl = async (url: string, fallbackFileName?: string) => {
  try {
    const response = await fetch(url);
    const contentDisposition = response.headers.get('Content-Disposition');
    const fileName = contentDisposition
      ? parseContentDisposition(contentDisposition).parameters['filename']
      : fallbackFileName || 'unknown';
    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type });
  } catch (err) {
    console.error(err);
    return null;
  }
};
